/**
 * En-tête de l'éditeur avancé d'exercice : retour, titre, indicateur
 * d'auto-save, export/import IA et bascule de l'aperçu (extrait de
 * ExerciceAdvancedEditor).
 */
import React from 'react';
import { Box, Typography, Button, CircularProgress, IconButton, Tooltip, Chip } from '@mui/material';
import {
  ArrowBack, Visibility, Edit, Error as ErrorIcon, CloudDone, ContentCopy, FileDownload,
} from '@mui/icons-material';
import { Exercice } from '../../../types';
import { formatDate } from '../../../utils/dates';
import { ExerciceAdvancedEditorState } from './useExerciceAdvancedEditor';

interface EditorProps {
  editor: ExerciceAdvancedEditorState;
}

/** Indicateur d'auto-save : en cours, enregistré, erreur. */
const AutoSaveIndicator: React.FC<EditorProps> = ({ editor }) => {
  const { autoSaving, lastSaved, autoSaveError, clearAutoSaveError } = editor;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
      {autoSaving && (
        <Chip
          size="small"
          icon={<CircularProgress size={14} color="inherit" />}
          label="Enregistrement..."
          color="default"
          variant="outlined"
        />
      )}
      {!autoSaving && lastSaved && !autoSaveError && (
        <Chip
          size="small"
          icon={<CloudDone fontSize="small" />}
          label={`Enregistré à ${formatDate(lastSaved, 'time')}`}
          color="success"
          variant="outlined"
        />
      )}
      {autoSaveError && (
        <Tooltip title={autoSaveError}>
          <Chip
            size="small"
            icon={<ErrorIcon fontSize="small" />}
            label="Erreur"
            color="error"
            variant="outlined"
            onDelete={clearAutoSaveError}
          />
        </Tooltip>
      )}
    </Box>
  );
};

/** Boutons « Exporter IA » / « Importer IA ». */
const AIButtons: React.FC<EditorProps> = ({ editor }) => (
  <>
    <Tooltip title="Copier l'exercice pour correction IA">
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ContentCopy />}
        onClick={editor.handleExportForAI}
        sx={{ mr: 1 }}
      >
        Exporter IA
      </Button>
    </Tooltip>

    <Tooltip title="Importer les corrections de l'IA">
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<FileDownload />}
        onClick={() => editor.setImportDialogOpen(true)}
        sx={{ mr: 1 }}
      >
        Importer IA
      </Button>
    </Tooltip>
  </>
);

/** En-tête complet de l'éditeur avancé. */
const EditorHeader: React.FC<EditorProps & { exercice: Exercice }> = ({ editor, exercice }) => {
  const { handleCancel, canUseAICorrection, isReadOnly, showPreview, setShowPreview } = editor;
  return (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <IconButton onClick={handleCancel} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Édition Avancée: {exercice.titre}
        </Typography>

        {/* Indicateur d'auto-save */}
        <AutoSaveIndicator editor={editor} />

        {/* Boutons Export/Import IA */}
        {canUseAICorrection() && !isReadOnly && <AIButtons editor={editor} />}

        <Tooltip title={showPreview ? 'Masquer la prévisualisation' : 'Afficher la prévisualisation'}>
          <IconButton onClick={() => setShowPreview(!showPreview)} color="primary">
            {showPreview ? <Visibility /> : <Edit />}
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="textSecondary">
        Modifiez l'exercice - les changements sont enregistrés automatiquement
      </Typography>
    </Box>
  );
};

export default EditorHeader;
